import React, { useState, useEffect } from "react";
import { Code, FileText, Menu, X } from "lucide-react";

export default function Sidebar({
  languages,
  currentLanguage,
  setCurrentLanguage,
  lessons,
  currentLesson,
  setCurrentLesson,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if it's mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Close sidebar when clicking on overlay or lesson
  const handleSidebarClose = () => {
    setIsMobileOpen(false);
  };

  // Handle lesson click for mobile
  const handleLessonClick = (lesson) => {
    setCurrentLesson(lesson);
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  // Handle language change for mobile
  const handleLanguageChange = (lang) => {
    setCurrentLanguage(lang);
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  // Hamburger menu component for mobile
  const HamburgerButton = () => (
    <button
      className="md:hidden fixed top-14 left-4 z-50 p-2 bg-gray-900 rounded-md text-red-400 border"
      onClick={() => setIsMobileOpen(!isMobileOpen)}
    >
      {isMobileOpen ? <X className="w-2 h-2" /> : <Menu className="w-2 h-2" aria-label="hamburger menu" />}
    </button>
  );

  // Sidebar content component
  const SidebarContent = () => (
    <div className="w-64 bg-gray-900 h-[calc(100vh-4rem)] p-4 flex flex-col">
      {/* Programming Languages Section - Fixed height */}
      <div className="mb-4 shrink-0">
        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <h2 className="text-sm border pl-2 pr-2 pb-1 pt-1 rounded-2xl text-cyan-400 font-bold">
            Select preferred Language:
          </h2>
          <span className="text-sm text-cyan-400">
            {isDropdownOpen ? "▲" : "▼"}
          </span>
        </div>
        {isDropdownOpen && (
          <div className="flex flex-col mt-2 space-y-2">
            {languages.map((lang) => (
              <button
                key={lang}
                className={`py-1 px-3 rounded flex items-center space-x-2 ${
                  currentLanguage === lang
                    ? "bg-green-800 text-white font-semibold"
                    : "bg-black hover:bg-pink-900 cursor-pointer"
                }`}
                onClick={() => handleLanguageChange(lang)}
              >
                <Code className="w-4 h-4" />
                <span>{lang}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lessons Section - Fixed below programming languages, only this part scrollable */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <h2 className="text-lg text-cyan-400 font-bold mb-2 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Lessons
        </h2>
        
        {/* Scrollable Lesson List - Only this part scrolls */}
        <div className="flex-1 overflow-auto custom-scrollbar">
          <ul className="flex flex-col bg-black">
            {lessons.map((lesson, index) => (
              <li key={lesson.title} className="border-b border-gray-700 last:border-b-0">
                <button
                  className={`w-full text-left py-2 px-3 rounded flex items-center space-x-3 transition-all ${
                    currentLesson.title === lesson.title
                      ? "bg-green-800 font-semibold text-white"
                      : "hover:bg-pink-900 text-gray-300"
                  }`}
                  onClick={() => handleLessonClick(lesson)}
                >
                  <FileText className="w-4 h-4" />
                  <span className="flex-1 text-sm">{lesson.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Hamburger Button for Mobile */}
      <HamburgerButton />

      {/* Desktop Sidebar - Exactly as before */}
      <div className="hidden md:block">
        <SidebarContent />
      </div>

      {/* Mobile Sidebar with Overlay */}
      {isMobile && (
        <>
          {/* Overlay */}
          {isMobileOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={handleSidebarClose}
            />
          )}
          
          {/* Mobile Sidebar - 80% height */}
          <div 
            className={`
              fixed top-16 left-0 h-[60vh] z-40 md:hidden
              transform transition-transform duration-300 ease-in-out
              ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
            `}
          >
            <SidebarContent />
          </div>
        </>
      )}
    </>
  );
}